import { Card, Text, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase, CardContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useState } from "react";

import { PlacementSectionTemplate } from "./PlacementSectionTemplate";
import { WorkplacePhotosFullScreenDialog } from "./WorkplacePhotosFullScreenDialog";

interface WorkplacePhotosSectionProps {
  photos: string[];
  placementId: string;
  placementCandidateId: string;
  workerId: string;
}

interface PhotoTileProps {
  src: string;
  index: number;
  totalPhotos: number;
  aspectRatio?: string;
  fillHeight?: boolean;
  onPress: (index: number) => void;
  remainingPhotosCount?: number;
}

function PhotoTile(props: PhotoTileProps) {
  const { src, index, totalPhotos, aspectRatio, fillHeight, onPress, remainingPhotosCount } = props;

  return (
    <ButtonBase
      aria-label={`Open workplace photo ${index + 1} of ${totalPhotos}`}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        width: "100%",
        display: "block",
        ...(fillHeight && { height: "100%" }),
      }}
      onClick={() => {
        onPress(index);
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          ...(fillHeight ? { height: "100%" } : { aspectRatio }),
        }}
      >
        <Box
          component="img"
          src={src}
          alt={`Workplace photo ${index + 1}`}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 3,
            display: "block",
          }}
        />
        {isDefined(remainingPhotosCount) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text variant="h6" sx={{ color: "common.white" }}>
              +{remainingPhotosCount} more
            </Text>
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}

export function WorkplacePhotosSection(props: WorkplacePhotosSectionProps) {
  const { photos, placementId, placementCandidateId, workerId } = props;

  const photoViewerModalState = useModalState();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS,
    {
      action: "workplace_photos_section_viewed",
      photoCount: photos.length,
      placementId,
      placementCandidateId,
      workerId,
    },
    {
      enabled: photos.length >= 4,
    }
  );

  function handlePhotoPress(index: number) {
    setCurrentPhotoIndex(index);
    photoViewerModalState.openModal();

    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "workplace_photo_viewed",
      trigger: "tap",
      photoIndex: index + 1,
      photoCount: photos.length,
      placementId,
      placementCandidateId,
      workerId,
      source: "workplace_photos_section",
    });
  }

  function handlePhotoIndexChange(index: number, trigger: "swipe" | "tap") {
    setCurrentPhotoIndex(index);

    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "workplace_photo_viewed",
      trigger,
      photoIndex: index + 1,
      photoCount: photos.length,
      placementId,
      placementCandidateId,
      workerId,
      source: "workplace_photos_carousel",
    });
  }

  if (photos.length < 4) {
    return null;
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ pb: 5 }}>
            <PlacementSectionTemplate title="Photos" iconType="camera-filled" />
          </Box>
          {/* Adding all cases for future case where we may support less than 4 photos */}
          {photos.length === 1 && (
            <PhotoTile
              src={photos[0]}
              index={0}
              totalPhotos={photos.length}
              aspectRatio="16/9"
              onPress={(index) => handlePhotoPress(index)}
            />
          )}

          {photos.length === 2 && (
            <Stack direction="row" spacing={2}>
              <PhotoTile
                src={photos[0]}
                index={0}
                totalPhotos={photos.length}
                aspectRatio="1/1"
                onPress={(index) => handlePhotoPress(index)}
              />
              <PhotoTile
                src={photos[1]}
                index={1}
                totalPhotos={photos.length}
                aspectRatio="1/1"
                onPress={(index) => handlePhotoPress(index)}
              />
            </Stack>
          )}

          {photos.length >= 3 && (
            <Box sx={{ aspectRatio: "16/9" }}>
              <Stack direction="row" spacing={2} sx={{ height: "100%" }}>
                <Box sx={{ flex: 3, height: "100%" }}>
                  <PhotoTile
                    fillHeight
                    src={photos[0]}
                    index={0}
                    totalPhotos={photos.length}
                    onPress={(index) => handlePhotoPress(index)}
                  />
                </Box>
                <Stack spacing={2} sx={{ flex: 2, height: "100%" }}>
                  <Box sx={{ flex: 1, height: 0 }}>
                    <PhotoTile
                      fillHeight
                      src={photos[1]}
                      index={1}
                      totalPhotos={photos.length}
                      onPress={(index) => handlePhotoPress(index)}
                    />
                  </Box>
                  <Box sx={{ flex: 1, height: 0 }}>
                    <PhotoTile
                      fillHeight
                      src={photos[2]}
                      index={2}
                      totalPhotos={photos.length}
                      // Remaining count excludes the 2 photos already visible in the right column
                      remainingPhotosCount={photos.length > 3 ? photos.length - 2 : undefined}
                      onPress={(index) => handlePhotoPress(index)}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
      <WorkplacePhotosFullScreenDialog
        photos={photos}
        modalState={photoViewerModalState}
        currentPhotoIndex={currentPhotoIndex}
        onPhotoIndexChange={(index, trigger) => handlePhotoIndexChange(index, trigger)}
      />
    </>
  );
}
